import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { normalizeError } from '@/shared/utils/normalizeError';

/**
 * Custom hook for consistent error handling across the app
 *
 * Usage:
 * const { handleError } = useErrorHandler();
 * try {
 *   await apiCall();
 * } catch (error) {
 *   handleError(error);  // Automatically displays user-friendly message
 * }
 */
export const useErrorHandler = () => {
  /**
   * Handle error with automatic toast notification
   * @param error - Any error object (Axios, Error, string)
   * @param customMessage - Optional custom message to override default
   */
  const handleError = useCallback((error: unknown, customMessage?: string) => {
    const message = customMessage || normalizeError(error);
    toast.error(message);

    // Log to console in development
    if (import.meta.env.MODE === 'development') {
      console.error('Error:', error);
    }
  }, []);

  /**
   * Handle error silently (no toast, just logging)
   * Useful for background operations
   */
  const handleSilentError = useCallback((error: unknown) => {
    console.error('Silent error:', error);
  }, []);

  return {
    handleError,
    handleSilentError,
  };
};
